import { range } from '@conversed/shared/src/helpers/data.helpers';
import type React from 'react';
import { useState } from 'react';

type TRatingProps = {
  maxRating: number;
  initialRatingValue: number;
  readOnly?: boolean;
  onSendRatingValue: (val: number) => void;
};

const FilledStar = () => {
  return (
    <svg
      role='img'
      aria-label='filled-star'
      className='rating-star filled-star'
      viewBox='0 0 24 24'>
      <path d='M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z' />
    </svg>
  );
};

const EmptyStar = () => {
  return (
    <svg
      role='img'
      aria-label='empty-star'
      className='rating-star empty-star'
      viewBox='0 0 24 24'>
      <path d='M22 9.24l-7.19-.62L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24zM12 15.4l-3.76 2.27 1-4.28-3.32-2.88 4.38-.38L12 6.1l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.4z' />
    </svg>
  );
};

type IRatingStarProps = {
  isFilled: boolean;
  isHovered: boolean;
};

const RatingStar: React.FC<IRatingStarProps> = ({ isFilled, isHovered }) => {
  if (isFilled) {
    return <FilledStar />;
  }

  if (!isFilled && !isHovered) {
    return <EmptyStar />;
  }

  if (isHovered) {
    return <FilledStar />;
  }

  return <EmptyStar />;
};

export const Rating: React.FC<TRatingProps> = ({
  maxRating,
  initialRatingValue,
  onSendRatingValue,
  readOnly = false,
}) => {
  const [rating, setRating] = useState(initialRatingValue);
  const [hoverValue, setHoverValue] = useState(0);

  const handleMouseEnter = (value: number) => {
    if (readOnly) return;
    setHoverValue(value);
  };

  const handleMouseLeave = () => {
    if (readOnly) return;
    setHoverValue(rating);
  };

  const handleClickSelect = (value: number) => {
    if (readOnly) return;
    setRating(value);
    onSendRatingValue(value);
  };

  return (
    <>
      <div className='rating-container'>
        {range(1, maxRating).map((starValue) => {
          const isFilled = starValue <= rating;
          const isHovered = starValue <= hoverValue;

          return (
            <span
              key={starValue}
              onClick={() => handleClickSelect(starValue)}
              onMouseEnter={() => handleMouseEnter(starValue)}
              onMouseLeave={handleMouseLeave}>
              <RatingStar isFilled={isFilled} isHovered={isHovered} />
            </span>
          );
        })}
      </div>
    </>
  );
};
